import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { SHOTS } from "@/lib/site";

const REAL = [
  { src: SHOTS.welcome, alt: "STETH welcome screen: Off call for work, on call for love", label: "Welcome" },
  { src: SHOTS.verified, alt: "Verified profile: Marcus, 30, ER Nurse", label: "Verified pros" },
  { src: SHOTS.firstdate, alt: "Profile card: Ava, 29", label: "Real profiles" },
  { src: SHOTS.chat, alt: "Chat: Start the conversation", label: "Conversation" },
  { src: SHOTS.onboarding, alt: "Onboarding: a profile that's really you", label: "Onboarding" },
];

const DIVERSE = [
  {
    photo: "https://images.unsplash.com/photo-1734968758338-7534a4c8ea72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxvbGRlciUyMHdvbWFuJTIwc21pbGluZyUyMHNpbHZlciUyMGhhaXIlMjBwb3J0cmFpdCUyMHdhcm0lMjBsaWdodHxlbnwwfHx8fDE3OTEwMDE3MjB8MA&ixlib=rb-4.1.0&q=85",
    alt: "Profile: Elena, 54, Midwife",
    label: "Every age",
    bg: "#F4D9CF",
    title: "Love at any age",
    titleColor: "#C4553F",
    name: "Elena, 54",
    chips: ["Midwife", "3 miles away", "Early shift"],
  },
  {
    photo: "https://images.unsplash.com/photo-1611403119860-57c4937ef987?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA8Mzl8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMGFzaWFuJTIwbWFuJTIwcG9ydHJhaXQlMjA0MHMlMjB3YXJtfGVufDB8fHx8MTc5MTAwMTcyMHww&ixlib=rb-4.1.0&q=85",
    alt: "Profile: Kenji, 45, Surgeon",
    label: "Every specialty",
    bg: "#241A16",
    title: "Second shifts, first dates",
    titleColor: "#F2CC8F",
    name: "Kenji, 45",
    chips: ["Surgeon", "4 miles away", "Night shift"],
  },
  {
    photo: "https://images.unsplash.com/photo-1536416992256-1c91ce9ccdfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxqb3lmdWwlMjBibGFjayUyMHdvbWFuJTIwcG9ydHJhaXQlMjB3YXJtJTIwbGlnaHR8ZW58MHx8fHwxNzkxMDAxNzIwfDA&ixlib=rb-4.1.0&q=85",
    alt: "Profile: Nia, 31, Therapist",
    label: "Every background",
    bg: "#A9CDBB",
    title: "Real people only",
    titleColor: "#FFFFFF",
    sub: "Get verified, and filter for verified pros",
    subColor: "#2E5B45",
    name: "Nia, 31",
    chips: ["Therapist", "1 mile away", "Day shift"],
  },
  {
    photo: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsYXRpbmElMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdCUyMHdhcm0lMjBsaWdodHxlbnwwfHx8MTc5MTAwMTcyMHww&ixlib=rb-4.1.0&q=85",
    alt: "Profile: Camila, 36, Dentist",
    label: "Every story",
    bg: "#F4D9CF",
    title: "Your person is here",
    titleColor: "#C4553F",
    name: "Camila, 36",
    chips: ["Dentist", "2 miles away", "Weekends off"],
  },
];

// Interleave real App Store screenshots with diverse composed frames
const FRAMES = [
  { kind: "img", ...REAL[0] },
  { kind: "card", ...DIVERSE[0] },
  { kind: "img", ...REAL[1] },
  { kind: "card", ...DIVERSE[1] },
  { kind: "img", ...REAL[2] },
  { kind: "card", ...DIVERSE[2] },
  { kind: "img", ...REAL[3] },
  { kind: "card", ...DIVERSE[3] },
  { kind: "img", ...REAL[4] },
];

const ROTATIONS = ["-7deg", "-5deg", "-3.5deg", "-1.5deg", "0deg", "1.5deg", "3.5deg", "5deg", "7deg"];

const ComposedShot = ({ bg, title, titleColor, sub, subColor, photo, name, chips, alt }) => (
  <div className="relative h-full w-full" style={{ background: bg }}>
    <div className="px-5 pt-9 text-center">
      <div className="font-editorial text-[1.45rem] font-semibold leading-[1.15]" style={{ color: titleColor }}>
        {title}
      </div>
      {sub && (
        <div className="mx-auto mt-2 max-w-[150px] text-[9.5px] font-bold leading-snug" style={{ color: subColor }}>
          {sub}
        </div>
      )}
    </div>
    <div className="absolute inset-x-4 bottom-5 overflow-hidden rounded-[1.2rem] shadow-[0_25px_45px_-20px_rgba(36,26,22,0.55)]">
      <img src={photo} alt={alt} loading="lazy" className="aspect-[3/3.7] w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/85" />
      <div className="absolute bottom-2.5 left-3 right-3">
        <div className="flex items-center gap-1.5 font-editorial text-lg font-semibold text-white">
          {name}
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sage">
            <Check size={10} strokeWidth={3.5} className="text-white" />
          </span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {chips.map((c) => (
            <span key={c} className="rounded-full bg-black/45 px-2 py-0.5 text-[8px] font-bold text-white">
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const Screenshots = () => (
  <section
    id="screenshots"
    data-testid="screenshots-section"
    className="overflow-hidden bg-gradient-to-b from-sand via-peach/20 to-sand py-28 sm:py-36"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Eyebrow>Straight from the app</Eyebrow>
        <h2 className="mt-5 font-editorial text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
          Real screens,{" "}
          <span className="font-normal italic text-terracotta-dark">real people</span>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-clay sm:text-lg">
          No mockups, no renders. The actual app on the App Store, and the
          people of every age and background you will find inside.
        </p>
      </Reveal>

      <div className="mt-16 flex snap-x snap-mandatory items-end gap-5 overflow-x-auto px-2 pb-10 pt-6 md:mt-20 md:justify-center md:gap-0 md:overflow-visible md:px-0">
        {FRAMES.map((f, i) => (
          <motion.figure
            key={f.label}
            data-testid={`screenshot-frame-${i}`}
            initial={{ opacity: 0, y: 70, rotate: ROTATIONS[i] }}
            whileInView={{ opacity: 1, y: 0, rotate: ROTATIONS[i] }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ rotate: "0deg", y: -16, scale: 1.06, zIndex: 30 }}
            className={`relative w-40 shrink-0 snap-center overflow-hidden rounded-[1.6rem] border border-hairline bg-cream shadow-[0_35px_70px_-30px_rgba(36,26,22,0.5)] sm:w-48 md:w-44 ${
              i > 0 ? "md:-ml-14" : ""
            } ${i === 4 ? "md:z-10 md:w-52" : ""}`}
            style={{ zIndex: i === 4 ? 10 : i }}
          >
            <div className="aspect-[9/19.5] w-full">
              {f.kind === "img" ? (
                <img src={f.src} alt={f.alt} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <ComposedShot {...f} />
              )}
            </div>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);
